import { ArrowRight } from 'lucide-react'
import { cn } from '@/lib/cn'
import { formatNaira } from '@/lib/format'
import type { Balances } from '../types'

interface Props {
  balances: Balances
}

/**
 * AF-125 — before → after balance display with the change highlighted.
 * If neither side has balance data, render nothing.
 */
export function BalancePanel({ balances }: Props) {
  const hasSource =
    balances.source_before_minor !== null && balances.source_after_minor !== null
  const hasDest =
    balances.destination_before_minor !== null &&
    balances.destination_after_minor !== null

  if (!hasSource && !hasDest) return null

  return (
    <div className="grid gap-2 sm:grid-cols-2">
      {hasSource && (
        <BalanceRow
          label="Source"
          before={balances.source_before_minor!}
          after={balances.source_after_minor!}
        />
      )}
      {hasDest && (
        <BalanceRow
          label="Destination"
          before={balances.destination_before_minor!}
          after={balances.destination_after_minor!}
        />
      )}
    </div>
  )
}

function BalanceRow({
  label,
  before,
  after,
}: {
  label: string
  before: number
  after: number
}) {
  const delta = after - before
  const positive = delta > 0
  const negative = delta < 0
  return (
    <div className="rounded-md border border-border bg-card p-3">
      <div className="text-xs uppercase tracking-wide text-muted-fg">{label}</div>
      <div className="mt-1 flex items-center gap-2 font-mono text-sm">
        <span className="text-muted-fg">{formatNaira(before)}</span>
        <ArrowRight size={14} className="text-muted-fg" aria-hidden />
        <span className="font-medium text-fg">{formatNaira(after)}</span>
      </div>
      <div
        className={cn(
          'mt-1 text-xs',
          positive && 'text-status-success',
          negative && 'text-destructive',
          !positive && !negative && 'text-muted-fg',
        )}
      >
        {positive ? '+' : ''}
        {formatNaira(delta)}
      </div>
    </div>
  )
}
